import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import { PageTitle } from '@shared/ui';
import { Table } from '@widgets/table';
import type { IDoctor } from '@app/interfaces/doctor.interface';
import type { IClinic } from '@app/interfaces/clinic.interface';
import {
  addDoctor,
  deleteDoctor,
  fetchDoctors,
  fetchDoctorsByClinic,
  updateDoctor,
} from '@app/api/doctor';
import { fetchClinics } from '@app/utils/clinic';
import globalAppStore from '@app/stores/globalAppStore';
import DoctorEditForm from '@app/pages/clinicPage/components/doctorEditForm/DoctorEditForm';

const columnHelper = createColumnHelper<IDoctor>();

export function DoctorListPage() {
  const [doctors, setDoctors] = useState<IDoctor[]>([]);
  const [clinics, setClinics] = useState<IClinic[]>([]);
  const [clinic, setClinic] = useState<IClinic>();
  const [selectedDoctor, setSelectedDoctor] = useState<IDoctor>();
  const [selectedClinicIds, setSelectedClinicIds] = useState<string[]>([]);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState<number>();

  useEffect(() => {
    fetchClinics().then(({ clinics }) => setClinics(clinics || []));
  }, []);

  const loadDoctors = useCallback(async () => {
    setIsLoading(true);
    const doctors = clinic ? await fetchDoctorsByClinic(clinic.id) : await fetchDoctors();

    setDoctors(
      (doctors || []).map((doctor: IDoctor) => ({
        ...doctor,
        clinic: doctor.clinic || clinic,
        clinics: doctor.clinics?.length ? doctor.clinics : clinic ? [clinic] : [],
      }))
    );
    setIsLoading(false);
  }, [clinic]);

  useEffect(() => {
    loadDoctors();
  }, [loadDoctors]);

  const selectClinic = (event: SelectChangeEvent) => {
    const clinic = clinics.find((item) => item.id === +event.target.value);
    setClinic(clinic);
  };

  const openCreateDialog = () => {
    setSelectedDoctor(undefined);
    setSelectedClinicIds(clinic ? [clinic.id.toString()] : []);
    setIsEditorOpen(true);
  };

  const openEditDialog = useCallback(
    (doctor: IDoctor) => {
      setSelectedDoctor(doctor);
      setSelectedClinicIds(
        doctor.clinics?.length
          ? doctor.clinics.map((clinic) => clinic.id.toString())
          : doctor.clinic
            ? [doctor.clinic.id.toString()]
            : []
      );
      setIsEditorOpen(true);
    },
    []
  );

  const closeEditor = () => {
    setIsEditorOpen(false);
    setSelectedDoctor(undefined);
    setSelectedClinicIds([]);
  };

  const handleDoctorClinicsChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedClinicIds(typeof value === 'string' ? value.split(',') : value);
  };

  const saveDoctor = async (doctor: Partial<IDoctor>) => {
    if (!selectedClinicIds.length) {
      return;
    }

    const doctorWithClinics = {
      ...doctor,
      clinics: selectedClinicIds.map((id) => ({ id: +id }) as IClinic),
    };

    if (doctor.id) {
      await updateDoctor(doctorWithClinics);
    } else {
      await addDoctor(+selectedClinicIds[0], doctorWithClinics);
    }

    await loadDoctors();
  };

  const handleDelete = useCallback(
    (doctor: IDoctor) => {
      globalAppStore.updateGlobalConfirmPopupState({
        isVisible: true,
        title: 'Удаление доктора',
        text: `Вы уверены, что хотите удалить доктора ${doctor.fio}?`,
        subtext: '',
        confirmButtonTitle: 'Удалить',
        onConfirm: async () => {
          setIsDeletingId(doctor.id);
          await deleteDoctor(doctor);
          await loadDoctors();
          setIsDeletingId(undefined);
        },
      });
    },
    [loadDoctors]
  );

  const onRowClick = (row: Row<IDoctor>) => {
    openEditDialog(row.original);
  };

  const columns = useMemo<ColumnDef<IDoctor, string>[]>(
    () => [
      columnHelper.accessor('fio', {
        id: 'doctor-fio',
        header: 'ФИО',
      }),
      columnHelper.accessor('profession', {
        id: 'doctor-profession',
        header: 'Специальность',
      }),
      columnHelper.accessor('experience', {
        id: 'doctor-experience',
        header: 'Стаж',
      }),
      columnHelper.accessor(
        (doctor) =>
          doctor.clinics?.map((clinic) => clinic.name).join(', ') || doctor.clinic?.name || '',
        {
          id: 'doctor-clinics',
          header: 'Клиники',
        }
      ),
      columnHelper.display({
        id: 'doctor-actions',
        header: '',
        cell: ({ row }) => {
          const doctor = row.original;

          return (
            <Box onClick={(event) => event.stopPropagation()}>
              <IconButton
                size='small'
                aria-label='delete doctor'
                onClick={() => handleDelete(doctor)}
                disabled={isDeletingId === doctor.id}
              >
                {isDeletingId === doctor.id ? (
                  <CircularProgress size={16} color='error' />
                ) : (
                  <DeleteIcon fontSize='small' color='error' />
                )}
              </IconButton>
            </Box>
          );
        },
      }),
    ],
    [handleDelete, isDeletingId]
  );

  return (
    <Stack direction='column' spacing={2} className='content'>
      <PageTitle
        title='Доктора'
        action={
          <Button variant='outlined' onClick={openCreateDialog} disabled={!clinic}>
            Добавить
          </Button>
        }
      />

      <Stack direction='row' alignItems='center' spacing={4}>
        <FormControl size='small' fullWidth sx={{ width: '400px', maxWidth: '400px', flex: 1 }}>
          <InputLabel id='clinic-select'>Клиника</InputLabel>
          <Select
            labelId='clinic-select'
            id='clinic'
            label='Клиника'
            size='small'
            value={(clinic?.id || '').toString()}
            onChange={selectClinic}
          >
            <MenuItem value=''>Все клиники</MenuItem>
            {clinics.map((clinic) => (
              <MenuItem key={clinic.id} value={clinic.id}>
                {clinic.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Table<IDoctor>
          columns={columns}
          data={doctors}
          getRowId={(row) => row.id.toString()}
          onRowClick={onRowClick}
        />
      )}

      <Dialog open={isEditorOpen} onClose={closeEditor} fullWidth maxWidth='md'>
        <DialogTitle>{selectedDoctor ? 'Редактировать доктора' : 'Добавить доктора'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel id='doctor-clinic-select-label'>Клиники</InputLabel>
            <Select
              multiple
              labelId='doctor-clinic-select-label'
              label='Клиники'
              value={selectedClinicIds}
              onChange={handleDoctorClinicsChange}
              renderValue={(selected) =>
                selected
                  .map((id) => clinics.find((clinic) => clinic.id === +id)?.name)
                  .filter(Boolean)
                  .join(', ')
              }
            >
              {clinics.map((clinic) => {
                const clinicId = clinic.id.toString();

                return (
                  <MenuItem key={clinic.id} value={clinicId}>
                    <Checkbox checked={selectedClinicIds.includes(clinicId)} />
                    <ListItemText primary={clinic.name} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {selectedDoctor || selectedClinicIds.length ? (
            <DoctorEditForm
              key={selectedDoctor?.id || `new-${selectedClinicIds.join('-')}`}
              doctor={selectedDoctor}
              onSave={saveDoctor}
              onCancel={closeEditor}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Stack>
  );
}

export default DoctorListPage;
